import React from 'react';
import { Link } from 'react-router-dom';
import {
    ChevronDown, ChevronLeft, ChevronRight,
    ChevronsLeft, ChevronsRight, Search,
    Gavel, WifiHigh, PlayCircle, RotateCcw,
    LayoutGrid, Clock,
    Building2
} from 'lucide-react';
import i18n from '../../../../i18n';
import { getClientsAuctions } from '../../../../models/organization';
import '../../../../styles/components/sellers-list.scss';

const PAGE_SIZE_OPTIONS = [12, 24, 48];

// Update TIME_FILTERS at the top
const TIME_FILTERS = [
    { value: 'all', label: i18n.t('all_auctions') || 'All', icon: LayoutGrid, category: 'all' },
    { value: 'current', label: i18n.t('active_auctions') || 'Active', icon: PlayCircle, category: 'all' },
    { value: 'previous', label: i18n.t('previous_auctions') || 'Past', icon: RotateCcw, category: 'all' },
    { value: 'realestate', label: i18n.t('real_estate') || 'Real Estate', icon: Building2, category: '10' },
];

const ORDER_OPTIONS = [
    { value: 'name', label: i18n.t('name') || 'Name' },
    { value: 'auctions', label: i18n.t('auctions') || 'Auctions' },
];

export class SellersList extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            sellers: [],
            loading: false,
            orderBy: 'name',
            searchQuery: '',
            currentPage: 1,
            pageSize: 12,
            totalCount: 0,
            timeFilter: 'all',
            categoryFilter: 'all',   // ← new
            showOrderDropdown: false,
            showPageSizeDropdown: false,
        };
        this.orderRef = React.createRef();
        this.pageSizeRef = React.createRef();
        this.searchTimeout = null;
        this.handleClickOutside = this.handleClickOutside.bind(this);
    }

    componentDidMount() {
        window.scrollTo(0, 0);
        document.addEventListener('mousedown', this.handleClickOutside);
        this.loadSellers();
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
        if (this.searchTimeout) clearTimeout(this.searchTimeout);
    }

    handleClickOutside(e) {
        if (this.orderRef.current && !this.orderRef.current.contains(e.target))
            this.setState({ showOrderDropdown: false });
        if (this.pageSizeRef.current && !this.pageSizeRef.current.contains(e.target))
            this.setState({ showPageSizeDropdown: false });
    }

    async loadSellers() {
        this.setState({ loading: true });
        try {
            const { currentPage, pageSize, orderBy, searchQuery, timeFilter, categoryFilter } = this.state;

            let params = `page=${currentPage}&limit=${pageSize}&order=${orderBy}&search=${searchQuery}`;

            if (timeFilter !== 'all') params += `&timeFilter=${timeFilter}`;
            if (categoryFilter !== 'all') params += `&category=${categoryFilter}`;

            const response = await getClientsAuctions(params);
            this.setState({
                sellers: response?.data?.data || [],
                totalCount: response?.data?.total || 0,
                loading: false,
            });
        } catch (e) {
            this.setState({ loading: false });
        }
    }

    setOrderBy = (value) => {
        this.setState({ orderBy: value, showOrderDropdown: false, currentPage: 1 },
            () => this.loadSellers());
    };

    setTimeFilter = (value) => {
        // find the matching filter to get its category
        const filter = TIME_FILTERS.find(f => f.value === value);
        const categoryFilter = filter?.category || 'all';

        // for real estate tab, reset timeFilter to 'all' (show all statuses)
        const timeFilter = value === 'realestate' ? 'all' : value;

        this.setState({ timeFilter: value, categoryFilter, currentPage: 1 },
            () => this.loadSellers());
    };

    handleSearch = (e) => {
        const val = e.target.value;
        this.setState({ searchQuery: val });
        if (this.searchTimeout) clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
            this.setState({ currentPage: 1 }, () => this.loadSellers());
        }, 400);
    };

    setPageSize = (size) => {
        this.setState({ pageSize: size, currentPage: 1, showPageSizeDropdown: false },
            () => this.loadSellers());
    };

    goToPage = (page) => {
        const totalPages = Math.ceil(this.state.totalCount / this.state.pageSize);
        if (page < 1 || page > totalPages) return;
        this.setState({ currentPage: page }, () => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            this.loadSellers();
        });
    };

    renderPagination() {
        const { currentPage, pageSize, totalCount } = this.state;
        const totalPages = Math.ceil(totalCount / pageSize);
        if (totalPages <= 1) return null;

        const maxVisible = 5;
        let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let end = Math.min(totalPages, start + maxVisible - 1);
        if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);

        const pages = [];
        for (let i = start; i <= end; i++) pages.push(i);

        return (
            <div className="sl-pagination">
                <button className="sl-page-btn" disabled={currentPage === 1} onClick={() => this.goToPage(1)}>
                    <ChevronsLeft size={16} />
                </button>
                <button className="sl-page-btn" disabled={currentPage === 1} onClick={() => this.goToPage(currentPage - 1)}>
                    <ChevronLeft size={16} />
                </button>
                {pages.map(p => (
                    <button
                        key={p}
                        className={`sl-page-btn sl-page-btn--num ${p === currentPage ? 'sl-page-btn--active' : ''}`}
                        onClick={() => this.goToPage(p)}
                    >
                        {p}
                    </button>
                ))}
                <button className="sl-page-btn" disabled={currentPage === Math.ceil(totalCount / pageSize)} onClick={() => this.goToPage(currentPage + 1)}>
                    <ChevronRight size={16} />
                </button>
                <button className="sl-page-btn" disabled={currentPage === Math.ceil(totalCount / pageSize)} onClick={() => this.goToPage(Math.ceil(totalCount / pageSize))}>
                    <ChevronsRight size={16} />
                </button>
            </div>
        );
    }

    render() {
        const {
            sellers, loading, orderBy, searchQuery,
            currentPage, pageSize, totalCount,
            showOrderDropdown, showPageSizeDropdown, timeFilter
        } = this.state;

        const totalPages = Math.ceil(totalCount / pageSize);
        const rangeStart = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
        const rangeEnd = Math.min(currentPage * pageSize, totalCount);

        return (
            <div className="sl-page">
                <div className="container">

                    <h1 className="sl-title">{i18n.t('sellers_list')}</h1>

                    {/* ── Toolbar ── */}
                    <div className="sl-toolbar">
                        <div className="sl-toolbar__left">

                            {/* Order by */}
                            <div className="af-dropdown" ref={this.orderRef}>
                                <button
                                    className="af-dropdown__trigger"
                                    onClick={() => this.setState(s => ({ showOrderDropdown: !s.showOrderDropdown }))}
                                >
                                    <span>{i18n.t('order_by') || 'Order by'}</span>
                                    <ChevronDown size={14} className={`af-dropdown__chevron ${showOrderDropdown ? 'af-dropdown__chevron--open' : ''}`} />
                                </button>
                                {showOrderDropdown && (
                                    <div className="af-dropdown__menu af-dropdown__menu--sort">
                                        {ORDER_OPTIONS.map(opt => (
                                            <button
                                                key={opt.value}
                                                className={`af-dropdown__item ${orderBy === opt.value ? 'af-dropdown__item--active' : ''}`}
                                                onClick={() => this.setOrderBy(opt.value)}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Search */}
                            <div className="sl-search">
                                <input
                                    type="text"
                                    className="sl-search__input"
                                    placeholder={i18n.t('search') || 'Search'}
                                    value={searchQuery}
                                    onChange={this.handleSearch}
                                />
                                <Search size={15} className="sl-search__icon" />
                            </div>
                        </div>

                        <div className="sl-toolbar__right">

                            {/* ── Time filter tabs ── */}
                            <div className="sl-time-tabs">
                                {TIME_FILTERS.map(opt => {
                                    const Icon = opt.icon;
                                    return (
                                        <button
                                            key={opt.value}
                                            className={`sl-time-tab ${timeFilter === opt.value ? 'sl-time-tab--active' : ''}`}
                                            onClick={() => this.setTimeFilter(opt.value)}
                                        >
                                            <Icon size={15} />
                                            {opt.label}
                                        </button>
                                    );
                                })}
                            </div>

                            {totalCount > 0 && (
                                <span className="sl-range-label">
                                    {rangeStart}–{rangeEnd} {i18n.t('of') || 'of'} {totalCount}
                                </span>
                            )}

                            {/* Page size */}
                            <div className="af-dropdown" ref={this.pageSizeRef}>
                                <button
                                    className="af-dropdown__trigger"
                                    onClick={() => this.setState(s => ({ showPageSizeDropdown: !s.showPageSizeDropdown }))}
                                >
                                    <span>{pageSize}</span>
                                    <ChevronDown size={14} className={`af-dropdown__chevron ${showPageSizeDropdown ? 'af-dropdown__chevron--open' : ''}`} />
                                </button>
                                {showPageSizeDropdown && (
                                    <div className="af-dropdown__menu af-dropdown__menu--sort">
                                        {PAGE_SIZE_OPTIONS.map(size => (
                                            <button
                                                key={size}
                                                className={`af-dropdown__item ${pageSize === size ? 'af-dropdown__item--active' : ''}`}
                                                onClick={() => this.setPageSize(size)}
                                            >
                                                {size}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── Loading ── */}
                    {loading && (
                        <div className="sl-loading">
                            <div className="sl-spinner" />
                            <span>{i18n.t('loading') || 'Loading...'}</span>
                        </div>
                    )}

                    {/* ── Empty ── */}
                    {!loading && sellers.length === 0 && (
                        <div className="sl-empty">
                            <Gavel size={40} />
                            <p>{i18n.t('no_sellers') || 'No sellers found'}</p>
                        </div>
                    )}

                    {/* ── Grid ── */}
                    {!loading && sellers.length > 0 && (
                        <div className="sl-grid">
                            {sellers.map((seller, i) => (
                                <Link
                                    to={`/auctions?organization=${seller.id}`}
                                    className="sl-card"
                                    key={seller.id || i}
                                >
                                    <div className="sl-card__logo-wrap">
                                        <img
                                            src={seller.logo || seller.organization_image_url || (process.env.PUBLIC_URL + '/assets/images/placeholder.jpg')}
                                            alt={seller.name || ''}
                                            className="sl-card__logo"
                                        />
                                    </div>
                                    <div className="sl-card__info">
                                        <h3 className="sl-card__name">
                                            {i18n.language === 'ar' ? seller.name_ar : seller.name || '—'}
                                        </h3>
                                        <div className="sl-card__stats">
                                            {/* <span className="sl-card__stat">
                                                <Gavel size={13} />
                                                {i18n.t('number_of_auctions')} {seller.auction_count ?? 0}
                                            </span> */}
                                            {seller.active_auctions > 0 && (
                                                <span className="sl-card__stat sl-card__stat--active">
                                                    <PlayCircle size={13} />
                                                    {i18n.t('active_auctions')} {seller.active_auctions}
                                                </span>
                                            )}
                                            {seller.expired_auctions > 0 && (
                                                <span className="sl-card__stat sl-card__stat--expired">
                                                    <Clock size={13} />
                                                    {i18n.t('previous_auctions')} {seller.expired_auctions}
                                                </span>
                                            )}
                                            {seller.realestate_auctions > 0 && (   // ← new badge
                                                <span className="sl-card__stat sl-card__stat--realestate">
                                                    <Building2 size={13} />
                                                    {i18n.t('real_estate')} {seller.realestate_auctions}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}

                    {/* ── Pagination ── */}
                    {!loading && this.renderPagination()}
                </div>
            </div>
        );
    }
}

export default SellersList;